import { useQuery } from "@apollo/client";

import {
    MetricField,
    MyLensesAssetType,
    ReportingGranularity,
    ReportingSortDirection,
    type UserDimension,
} from "../../gql-generated/types";
import { type ApolloResult } from "../../shared/models";
import { type GetOrganicStatsQuery, type GetOrganicStatsQueryVariables, getOrganicStats } from "../api/queries";
import { type StartEndTimestamp } from "../converters";
import { createFieldListWithDimensions } from "../models";

import { useLensBreakdownAssetQueryParams } from "./useLensBreakdownAssetQueryParams";

export interface OrganicStatsQueryParams {
    metricFields: MetricField[];
    startEndTimestamp: StartEndTimestamp;
    isTotal: boolean;
    isAllTimeSelected: boolean;
    reportDimensions?: UserDimension[];
    skip?: boolean;
}

const deriveGranularity = (isAllTimeSelected: boolean, isTotal: boolean) => {
    if (isAllTimeSelected) {
        return isTotal ? ReportingGranularity.Lifetime : ReportingGranularity.Day;
    }

    return isTotal ? ReportingGranularity.Total : ReportingGranularity.Day;
};

// Preferred hook to call Organic Stats
export const useOrganicStatsQuery = ({
    metricFields,
    isTotal,
    reportDimensions = [],
    startEndTimestamp,
    isAllTimeSelected,
    skip,
}: OrganicStatsQueryParams): ApolloResult<{ data: GetOrganicStatsQuery | undefined }> => {
    const { lensId, breakdownAssetType } = useLensBreakdownAssetQueryParams();

    const { startTime, endTime } = startEndTimestamp;

    const { loading, data, error } = useQuery<GetOrganicStatsQuery, GetOrganicStatsQueryVariables>(getOrganicStats, {
        skip,
        variables: {
            granularity: deriveGranularity(isAllTimeSelected, isTotal),
            fieldsList: createFieldListWithDimensions(reportDimensions, metricFields),
            assetType: MyLensesAssetType.User,
            lensId,
            breakdownAssetType,
            startTime,
            endTime,
            sortBy: MetricField.MetricFieldUnset,
            sortDirection: ReportingSortDirection.SortDirectionUnset,
            limit: 0,
        },
    });

    return { loading, data, error };
};
